// Shared Sentry config for apps/auth — single source of truth for noise filters.
// Each regex is paired with the Sentry issue id it suppresses, sourced from real
// 14d top-freq events in web-auth (project id 4510959118581760).

export const ignoreErrors = [
  // IndexedDB user-environment noise — quota exceeded, db deleted, transactions racing.
  /Connection to Indexed Database server lost/i, // WEB-AUTH-11
  /Unable to open database file/i, // WEB-AUTH-R
  /Database deleted by request of the user/i, // WEB-AUTH-1R
  /Error creating Records table/i, // WEB-AUTH-1E
  /Attempt to open a cursor in database/i, // WEB-AUTH-16
  /An internal error was encountered in the Indexed Database server/i, // WEB-AUTH-1M

  // Browser/extension/native bridge noise.
  /ResizeObserver loop/i,
  /_AutofillCallbackHandler/, // WEB-AUTH-T / WEB-AUTH-14
  /Object Not Found Matching Id/i, // WEB-AUTH-1H
  /WKWebView was deallocated/, // WEB-AUTH-1D
  /notification\.is_permission_granted/, // WEB-AUTH-1W / 1X / 20 (Tauri/WKWebView native bridge)
  /TrustedTypePolicyFactory/, // WEB-AUTH-Y
  /Invalid call to runtime\.sendMessage\(\)/i, // WEB-AUTH-1Y
  /jQuery is not defined/i, // WEB-AUTH-26

  // Native-bridge noise — WebView/Tauri/native-shell runtime, not our JS (GitHub code search: zero hits in jusbrasil/web|jusia*).
  /window\.webkit\.messageHandlers/, // WEB-AUTH-2M / 2B (2B = upstream Microsoft Clarity #1066 in Meta IAB WebView)
  /WKWebView API client did not respond/, // WEB-AUTH-2C
  /nativeSecondaryActionHit/, // WEB-AUTH-2H
  /Can't find variable: _0x/i, // WEB-AUTH-21 (obfuscated injected third-party script)

  // Captcha integrations that load asynchronously.
  /grecaptcha is not defined/i, // WEB-AUTH-1V
  /hcaptcha is not defined/i, // WEB-AUTH-1P

  // Next.js framework noise.
  /ChunkLoadError/,
  /Loading chunk \d+ failed/,
  'NEXT_NOT_FOUND',
  'NEXT_REDIRECT',
  /^AbortError/, // WEB-AUTH-15

  // Plugin ACL noise from native shells.
  /not allowed by ACL/, // WEB-AUTH-1X
];

export const noiseErrorSubstrings = [
  'Load failed',
  'Failed to fetch',
  'NetworkError when attempting to fetch resource',
  'Network request failed',
  'Object captured as promise rejection with keys',
];

const BOT_UA =
  /bot|crawler|spider|HeadlessChrome|Lighthouse|ChatGPT-User|GPTBot|Bytespider|AhrefsBot/i;

// Synthetic bot farm UAs:
// 1. iPhone UAs claiming to be Chrome ("Chrome/") instead of CriOS ("CriOS/").
// 2. Chrome Mobile UAs with legacy/ancient major versions (< 70).
// Note: Discarding Chrome Mobile / WebViews with major < 70 is intentional.
export function isSyntheticUA(ua) {
  if (typeof ua !== 'string' || !ua) return false;

  // iOS Chrome uses 'CriOS/<version>', never 'Chrome/<version>'.
  if (/iPhone/i.test(ua) && /Chrome\//i.test(ua)) {
    return true;
  }

  // Chrome on Android/mobile reporting legacy major version (< 70).
  const chromeMatch = ua.match(/Chrome\/(\d+)/i);
  if (chromeMatch) {
    const major = parseInt(chromeMatch[1], 10);
    if (
      !isNaN(major) &&
      major < 70 &&
      (/Mobile/i.test(ua) || /Android/i.test(ua) || /Chrome Mobile/i.test(ua))
    ) {
      return true;
    }
  }

  return false;
}

// The signup token is base64({"email":...}), so an unscrubbed auth-route URL leaks
// the user's email into Sentry. String-level scrub keeps the URL shape intact.
const TOKEN_PATH_RE = /\/(cadastro\/email|recuperar-senha|resetar-senha)\/[^/?#]+/gi;
const INTERNAL_TOKEN_PATH_RE = /\/([a-z-]*token)\/[^/?#]+/gi;
const SENSITIVE_QS_RES = ['token', 'signUpByEmailToken', 'code', 'email'].map(
  (key) => new RegExp(`([?&]${key}=)[^&#]*`, 'gi')
);

export function scrubUrl(url) {
  if (typeof url !== 'string') return url;
  let out = url
    .replace(TOKEN_PATH_RE, '/$1/[token]')
    .replace(INTERNAL_TOKEN_PATH_RE, '/$1/[token]');
  for (const re of SENSITIVE_QS_RES) {
    out = out.replace(re, '$1[redacted]');
  }
  return out;
}

const scrubField = (obj, key) => {
  if (obj?.[key]) obj[key] = scrubUrl(obj[key]);
};

export function buildBeforeSend({ isClient }) {
  return (event) => {
    const value = event?.exception?.values?.[0]?.value || '';
    if (noiseErrorSubstrings.some((needle) => value.includes(needle))) return null;

    if (isClient) {
      // Checks both User-Agent (SDK default) and user-agent (defensive fallback for lowercase headers).
      const ua =
        event?.request?.headers?.['User-Agent'] ||
        event?.request?.headers?.['user-agent'] ||
        (typeof navigator !== 'undefined' ? navigator.userAgent : '');
      if (ua && (BOT_UA.test(ua) || isSyntheticUA(ua))) return null;
    }

    // Fail-open: a throw here would drop the whole event.
    try {
      const { request } = event ?? {};
      scrubField(request, 'url');
      scrubField(event, 'transaction');
      if (request?.query_string) request.query_string = undefined;
      // Referer/Referrer carries the previous page's token URL.
      for (const key of Object.keys(request?.headers ?? {})) {
        if (/^referr?er$/i.test(key)) scrubField(request.headers, key);
      }
      for (const crumb of event?.breadcrumbs ?? []) {
        scrubField(crumb, 'message');
        scrubField(crumb?.data, 'url');
        scrubField(crumb?.data, 'to');
        scrubField(crumb?.data, 'from');
      }
    } catch {
      /* keep the event */
    }

    return event;
  };
}

// Gate Sentry to production so staging/preview traffic does not inflate quota.
// Widen the predicate if staging is ever wired to the same DSN.
export const isProd = process.env.NEXT_PUBLIC_ENV === 'production';

// Span sampling: route-weighted tracesSampler.
// web-auth emits far more spans than the ~5M/mo project quota accepts. /login and
// middleware alone are ~91% of transaction volume (14d discover query, project
// 4510959118581760) — a flat rate either starves the rare-but-critical auth flows
// below of signal or blows the budget on those two routes. Rate is chosen per group.
export const DEFAULT_TRACES_SAMPLE_RATE = 0.0002;

const JUNK_TRANSACTIONS = [
  /_next\/static/i,
  /\/health(?:$|\/|\?)/i,
  /\/ping(?:$|\/|\?)/i,
  /favicon/i,
];

// Matched narrowly (not a bare "/login" prefix) so nested pages like /login/details
// — a distinct, much lower-volume route — aren't folded into the dominant bucket.
const DOMINANT_TRANSACTIONS = [/(?:^|\s)\/login\/?(?:$|\?)/i, /(?:^|\s)middleware$/i];

const CRITICAL_RARE_TRANSACTIONS = [
  /\/cadastro/i,
  /\/recuperar-senha/i,
  /\/resetar-senha/i,
  /signup/i,
  /oauth/i,
  /callback/i,
  /token/i,
  /alterar-senha/i,
  /alterar-email/i,
];

const INTERNAL_TRANSACTIONS = [/\/api\/_internal/i, /bootstrap-csrf/i, /\/api\//i];

const matchesAny = (patterns, name) => patterns.some((pattern) => pattern.test(name));

export function resolveTracesSampleRate(name) {
  if (!name) return DEFAULT_TRACES_SAMPLE_RATE;
  if (matchesAny(JUNK_TRANSACTIONS, name)) return 0;
  if (matchesAny(DOMINANT_TRANSACTIONS, name)) return 0.00005;
  if (matchesAny(CRITICAL_RARE_TRANSACTIONS, name)) return 0.0005;
  if (matchesAny(INTERNAL_TRANSACTIONS, name)) return 0.0001;
  return DEFAULT_TRACES_SAMPLE_RATE;
}
